package fr.frux.shared.dto

import kotlinx.serialization.ExperimentalSerializationApi
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.JsonClassDiscriminator
import kotlin.time.Instant

/**
 * Corps de `POST /api/v1/trees/{id}/corrections` (spec 2026-10-19) : une correction porte sur un
 * seul champ d'identité. Le discriminant est `kind` et non le `type` par défaut, pour que le corps
 * se lise comme le contrat de la spec. Un client Ktor doit sérialiser le type scellé, pas la
 * sous-classe, sinon le discriminant disparaît (`setBody<TreeCorrectionRequest>(...)`).
 */
@OptIn(ExperimentalSerializationApi::class)
@Serializable
@JsonClassDiscriminator("kind")
sealed interface TreeCorrectionRequest {
    /** Motif facultatif, 280 caractères au plus (`TreeCorrectionLimits.REASON_MAX`). */
    val reason: String?

    @Serializable
    @SerialName("species")
    data class Species(
        val speciesId: Long,
        val certainty: CertaintyDto,
        override val reason: String? = null,
    ) : TreeCorrectionRequest

    @Serializable
    @SerialName("position")
    data class Position(
        val latitude: Double,
        val longitude: Double,
        override val reason: String? = null,
    ) : TreeCorrectionRequest

    /** Chaîne vide (après trim serveur) = effacement de la note. */
    @Serializable
    @SerialName("note")
    data class Note(
        val description: String,
        override val reason: String? = null,
    ) : TreeCorrectionRequest
}

/** Pied du bloc Identité de la fiche. [authorName] nul pour une correction d'opérateur (ADR 0023). */
@Serializable
data class LastCorrectionDto(
    val authorName: String? = null,
    @Serializable(with = InstantEpochMillisSerializer::class)
    val createdAt: Instant,
)

/**
 * Une ligne de l'historique public (`GET /api/v1/trees/{id}/revisions`). [kind], [field] et
 * [source] suivent `fr.frux.shared.content.TreeRevisionVocabulary`. [before]/[after] gardent le
 * format de `tree_revisions` (`speciesId` et `certainty`, `latitude` et `longitude`, toutes en
 * chaînes), sauf la note : seul `notePresent` (`"true"`/`"false"`) en sort, jamais son texte.
 * [redacted] : valeurs retirées par la modération, vides ici.
 */
@Serializable
data class TreeRevisionDto(
    val id: Long? = null,
    val kind: String,
    val field: String? = null,
    val before: Map<String, String> = emptyMap(),
    val after: Map<String, String> = emptyMap(),
    val authorName: String? = null,
    val reason: String? = null,
    val source: String,
    val redacted: Boolean = false,
    @Serializable(with = InstantEpochMillisSerializer::class)
    val createdAt: Instant,
)

/**
 * Révisions de la plus récente à la plus ancienne, puis la ligne de création.
 * [truncated] : plus de 50 corrections existent.
 */
@Serializable
data class TreeRevisionsResponse(
    val revisions: List<TreeRevisionDto>,
    val truncated: Boolean = false,
)
